import { DEFAULT_LANG, type Lang } from '../i18n';

/**
 * Nastavitve, kot jih vidi aplikacija (T2 §2).
 *
 * Tip je celoten že zdaj, čeprav faza 2 uporablja le del — enako kot
 * `Station` v fazi 1. Razlog je izvoz: oblika kopije je verzionirana in
 * dodajanje polja pozneje pomeni migracijo, ki bi se ji dalo izogniti.
 *
 * ⚠️ **`theme` in `lang` v fazi 2 nista vir resnice.** Temo drži
 * `ds/js/theme.js` v svojem ključu, jezik pa `nesywave.lang` — oba se
 * bereta **sinhrono pred prvim izrisom**, česar asinhrona shramba ne zmore
 * brez utripa. Nastavitve ju prevzamejo v fazi 5, ko zaslon Nastavitve
 * dobi pravi preklop (D1). Do takrat sta tu samo zato, da ju izvoz zajame.
 */

export type EqPreset =
  | 'flat'
  | 'rock'
  | 'pop'
  | 'jazz'
  | 'classical'
  | 'bass'
  | 'treble'
  | 'vocal'
  | 'custom';

export type SortMode = 'custom' | 'az' | 'za' | 'recent';

export const SORT_MODES: readonly SortMode[] = ['custom', 'az', 'za', 'recent'];

export interface Settings {
  theme: 'dark' | 'light';
  lang: Lang;
  volume: number;
  muted: boolean;
  sortMode: SortMode;
  favoritesOnly: boolean;
  eqEnabled: boolean;
  eqPreset: EqPreset;
  /** 10 pasov, dB, −12..+12. Ojačitev je brand, ne status (CLAUDE.md, pravilo 3). */
  eqGains: number[];
  visualizerEnabled: boolean;
  carModeAutoHint: boolean;
  lastStationId?: string;
  /**
   * Ali je uporabnik seed postaje že izbrisal (T2 §9).
   *
   * Brez te zastavice bi se ob naslednjem zagonu vrnile — seed se naloži,
   * kadar shrambe še ni, in prazen seznam je od »še ni bilo shrambe«
   * nerazločljiv.
   */
  seedDismissed: boolean;
}

/**
 * Privzete vrednosti (CLAUDE.md, tabela privzetih nastavitev).
 *
 * EQ je raven in vizualizator izklopljen **namerno**: oboje bi postavilo
 * Web Audio graf in s tem postajo prisililo na proxy pot (T1 §4).
 */
export const DEFAULT_SETTINGS: Settings = {
  theme: 'dark',
  lang: DEFAULT_LANG,
  volume: 1,
  muted: false,
  sortMode: 'custom',
  favoritesOnly: false,
  eqEnabled: false,
  eqPreset: 'flat',
  eqGains: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  visualizerEnabled: false,
  carModeAutoHint: true,
  seedDismissed: false,
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function pickString<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}

function pickBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

/**
 * Nastavitve iz shrambe, s privzetimi vrednostmi za vse, česar ni ali je
 * napačnega tipa.
 *
 * Zakaj to ni ista stvar kot migracija: migracija pretvarja **znano staro
 * obliko** v novo. To tu je obramba pred zapisom, ki ga je pokvarilo nekaj
 * zunaj naše verzije — ročno urejanje, prekinjen zapis, uvoz tuje datoteke.
 * Migracija sme predpostaviti obliko, branje ne sme.
 */
export function parseSettings(raw: unknown): Settings {
  if (!isRecord(raw)) return { ...DEFAULT_SETTINGS };

  const gains = Array.isArray(raw.eqGains)
    ? raw.eqGains.filter((g): g is number => typeof g === 'number')
    : [];

  const settings: Settings = {
    theme: pickString(raw.theme, ['dark', 'light'] as const, DEFAULT_SETTINGS.theme),
    lang: pickString(
      raw.lang,
      ['en', 'sl'] as const satisfies readonly Lang[],
      DEFAULT_SETTINGS.lang,
    ),
    volume:
      typeof raw.volume === 'number' && raw.volume >= 0 && raw.volume <= 1
        ? raw.volume
        : DEFAULT_SETTINGS.volume,
    muted: pickBoolean(raw.muted, DEFAULT_SETTINGS.muted),
    sortMode: pickString(raw.sortMode, SORT_MODES, DEFAULT_SETTINGS.sortMode),
    favoritesOnly: pickBoolean(raw.favoritesOnly, DEFAULT_SETTINGS.favoritesOnly),
    eqEnabled: pickBoolean(raw.eqEnabled, DEFAULT_SETTINGS.eqEnabled),
    eqPreset: pickString(
      raw.eqPreset,
      ['flat', 'rock', 'pop', 'jazz', 'classical', 'bass', 'treble', 'vocal', 'custom'] as const,
      DEFAULT_SETTINGS.eqPreset,
    ),
    eqGains:
      gains.length === DEFAULT_SETTINGS.eqGains.length ? gains : [...DEFAULT_SETTINGS.eqGains],
    visualizerEnabled: pickBoolean(raw.visualizerEnabled, DEFAULT_SETTINGS.visualizerEnabled),
    carModeAutoHint: pickBoolean(raw.carModeAutoHint, DEFAULT_SETTINGS.carModeAutoHint),
    seedDismissed: pickBoolean(raw.seedDismissed, DEFAULT_SETTINGS.seedDismissed),
  };

  // `lastStationId` je neobvezen; `exactOptionalPropertyTypes` ne dovoli `undefined`.
  if (typeof raw.lastStationId === 'string') settings.lastStationId = raw.lastStationId;

  return settings;
}
